import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  reasonsToDeclineAllQuotes,
  reasonToDeclineReferredQuote,
  lossTypes,
  capacityTypes,
} from '@components/modals/DeclinationModal/DeclinationModal.constants';
import { Dialog } from '@components/dialog';
import { QuoteStatus, QuoteStatusId } from '@models/quotes';
import { NoteType } from '@models/note';
import { ToastType } from '@components/toast/ToastType.enum';
import * as styles from '@components/modals/Modal.styles';
import { Form, FormSection } from '@components/forms';
import { getFormFields } from '@components/modals/DeclinationModal/getFormFields';
import { useCreateQuoteStatusChange } from '@services/quotes';
import { useInsertNote } from '@services/note';
import { DeclinationModalFormValues } from '@components/modals/DeclinationModal/DeclinatonModalFormValues.type';
import { formatFormValues } from '@components/forms/formatFormValues';
import { QuoteStatusChangeReason } from '@models/quoteStatusChange';

type DeclinationReasonDetails = {
  carrier?: string;
  otherCarrier?: string;
  premium?: string | number;
  message?: string;
};

// `quoteStatusChangeReason.details` is loosely typed by the API (string | number | boolean |
// object | array), so narrow it at runtime instead of asserting a shape it may not have.
const toReasonDetails = (details: unknown): DeclinationReasonDetails => {
  if (typeof details !== 'object' || details === null || Array.isArray(details)) return {};
  const record = details as Record<string, unknown>;
  const asString = (value: unknown) => (typeof value === 'string' ? value : undefined);
  return {
    carrier: asString(record.carrier),
    otherCarrier: asString(record.otherCarrier),
    premium: typeof record.premium === 'number' || typeof record.premium === 'string' ? record.premium : undefined,
    message: asString(record.message),
  };
};

type DeclinationModalEditableProps = {
  isReadOnly?: false;
  isOpen: boolean;
  quoteId: string;
  policyNumber: string;
  currentStatusId: string;
  customerId: string;
  brokerageId: string;
  onCloseModal: () => void;
  hasInternalOnlyAccessRestriction: boolean;
  onSuccess?: () => void;
};

// Read-only view opened from a Declined milestone: populated from the stored
// `quoteStatusChangeReason`, so none of the submit-flow props are required.
type DeclinationModalReadOnlyProps = {
  isReadOnly: true;
  isOpen: boolean;
  onCloseModal: () => void;
  policyNumber?: string;
  reasonData: QuoteStatusChangeReason;
};

type DeclinationModalProps = DeclinationModalEditableProps | DeclinationModalReadOnlyProps;

export const DeclinationModal: React.FC<DeclinationModalProps> = (props) => {
  const { isOpen, onCloseModal, policyNumber } = props;
  const isReadOnly = props.isReadOnly === true;
  const reasonData = props.isReadOnly ? props.reasonData : undefined;
  const editable = props.isReadOnly ? undefined : props;

  const { createQuoteStatusChange, isPending: isLoading } = useCreateQuoteStatusChange();
  const { insertNoteAsync } = useInsertNote();
  const { enqueueSnackbar } = useSnackbar();

  const readOnlyDetails = toReasonDetails(reasonData?.details);

  const methods = useForm<DeclinationModalFormValues>({
    defaultValues: reasonData
      ? {
          reason: reasonData.reason,
          notes: reasonData.description ?? '',
          carrier: readOnlyDetails.carrier ?? '',
          otherCarrier: readOnlyDetails.otherCarrier ?? '',
          premium: readOnlyDetails.premium != null ? Number(readOnlyDetails.premium) : undefined,
          messageToBroker: readOnlyDetails.message ?? '',
        }
      : { shouldSkipEmail: 'false' },
    mode: isReadOnly ? 'onSubmit' : 'onChange',
  });

  const clearForm = () => {
    methods.reset({ reason: undefined });
  };

  const handleExit = () => {
    clearForm();
    onCloseModal();
  };

  const selectedReason: string | undefined = methods.watch('reason');

  // Read-only shows the full reason list so any previously-stored reason renders its label
  // in the disabled Select; editable narrows the list based on the quote's current status.
  const declinationReasons: string[] = isReadOnly
    ? reasonToDeclineReferredQuote.concat(reasonsToDeclineAllQuotes)
    : editable?.currentStatusId !== QuoteStatus.Referred.id
      ? reasonsToDeclineAllQuotes
      : reasonToDeclineReferredQuote.concat(reasonsToDeclineAllQuotes);

  // Only read `formState.isValid` for the editable flow — accessing it makes react-hook-form
  // run validation on the (disabled, required) fields, which is pointless in read-only mode.
  const isValid = isReadOnly ? true : methods.formState.isValid;

  const typeFieldItems: string[] = useMemo(() => {
    if (isReadOnly) return [];
    return selectedReason === 'Loss History' ? lossTypes : selectedReason === 'Capacity' ? capacityTypes : [];
  }, [isReadOnly, selectedReason]);

  const fields = useMemo(() => {
    const allFields = getFormFields({
      declinationReasons,
      typeFieldItems,
      hasInternalOnlyAccessRestriction: editable?.hasInternalOnlyAccessRestriction ?? false,
    });
    if (!isReadOnly) return allFields;

    // Hide fields that are workflow-only inputs (`confirmation`, `shouldSkipEmail`) or that
    // the decline flow never persists to `quoteStatusChangeReason` (`type`).
    const hiddenInReadOnly = ['confirmation', 'shouldSkipEmail', 'type'];
    return Object.fromEntries(Object.entries(allFields).filter(([name]) => !hiddenInReadOnly.includes(name)));
  }, [declinationReasons, editable?.hasInternalOnlyAccessRestriction, isReadOnly, typeFieldItems]);

  useEffect(() => {
    if (isReadOnly) return;
    methods.reset({
      ...methods.getValues(),
      reason: selectedReason,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReason]);

  const handleDeclineQuote = async () => {
    if (!editable) {
      return;
    }

    const values = methods.getValues();
    const formData = formatFormValues(values, fields, { visibleOnly: true });

    const messageToBroker = formData.messageToBroker;
    const declineQuoteBody = {
      quoteId: editable.quoteId,
      statusId: QuoteStatusId.Declined,
      quoteStatusChangeReason: {
        reason: formData.reason,
        description: formData.notes,
        details: {
          carrier: formData.carrier,
          otherCarrier: formData.otherCarrier,
          premium: formData.premium,
          message: messageToBroker,
        },
      },
      shouldSkipEmail: formData.shouldSkipEmail,
    };

    createQuoteStatusChange(declineQuoteBody, {
      onError: () => {
        enqueueSnackbar(`Declination of quote #${policyNumber} has failed.`, {
          variant: ToastType.Error,
          preventDuplicate: true,
        });
      },
      onSuccess: () => {
        editable.onSuccess?.();
        if (messageToBroker) {
          const insertNoteBody = {
            type: NoteType.Message,
            body: messageToBroker,
            rawText: messageToBroker,
            quoteId: editable.quoteId,
            customerId: editable.customerId,
            brokerageId: editable.brokerageId,
          };
          insertNoteAsync({ noteRequestBody: insertNoteBody });
        }
        clearForm();
        onCloseModal();
        enqueueSnackbar(`Quote #${policyNumber} was successfully declined.`, {
          preventDuplicate: true,
          variant: ToastType.Success,
        });
      },
    });
  };

  const title = isReadOnly
    ? policyNumber
      ? `Declination Reasons for Quote #${policyNumber}`
      : 'Declination Reasons'
    : `Declination of Quote #${policyNumber}`;

  return (
    <Dialog
      handleCloseDialog={isReadOnly ? onCloseModal : handleExit}
      isPrimaryBtnDisabled={!isValid}
      isPrimaryBtnLoading={isLoading}
      open={isOpen}
      primaryBtnAction={isReadOnly ? undefined : handleDeclineQuote}
      primaryBtnText={isReadOnly ? undefined : 'Decline Quote'}
      secondaryBtnAction={isReadOnly ? onCloseModal : handleExit}
      secondaryBtnText='Exit'
      title={title}
    >
      <Form methods={methods} formId='declination-modal-form'>
        <Box sx={{ ...styles.modalContent, padding: 0 }}>
          <Box sx={styles.modalScrollContainer}>
            <FormSection fields={fields} styleConfig={{ spacing: 3 }} isReadOnly={isReadOnly} />
          </Box>
        </Box>
      </Form>
    </Dialog>
  );
};
